import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Blocks & the Blockchain',
    tagline: 'Why it\'s actually a chain, and what a block header proves.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what makes the blockchain a "chain" (hash pointer to previous block)
//   Q1 - what a Merkle root lets a lightweight client verify
//   Q2 - why altering an old block is so difficult
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 6 of 8" title="A block header points backward">
            <Prose>
                A block bundles a batch of transactions together with a header. That header contains a hash of the
                previous block's header, and it's that backward-pointing hash that actually creates "the chain."
                Each block cryptographically commits to everything that came before it.
            </Prose>
            <FactGrid items={[
                ['Previous block hash', 'Links this block to its parent, forming the chain.'],
                ['Merkle root', 'A single hash summarizing every transaction in the block.'],
                ['Timestamp & nonce', 'Used together with the difficulty target during mining (next stage).'],
            ]} columns={3} />
        </StageSection>
    )),

    question(0),

    content('merkle-tree', () => (
        <StageSection title="The Merkle root: one hash standing in for every transaction">
            <Prose>
                Rather than listing every transaction hash directly in the header, transactions are hashed together
                pairwise, repeatedly, up into a single Merkle root. This has a useful property: a lightweight client
                can be given a short "Merkle proof" (a small handful of hashes) and verify that one specific
                transaction is genuinely included in a block, without downloading or checking every other
                transaction in it. It also means changing a single transaction, even slightly, changes the Merkle
                root and is immediately detectable.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('why-hard-to-alter', () => (
        <StageSection title="Rewriting history means redoing the work">
            <Prose>
                Altering a transaction inside an already-mined block would change that block's hash. Since the next
                block's header points to that hash, the chain would immediately break at that point: every
                subsequent block would need to be re-mined too, referencing the new (altered) hash instead of the
                original. How expensive that re-mining actually is comes down to proof of work, covered next.
            </Prose>
            <KeyPoints items={[
                <>The hash chain gives tamper-evidence; proof of work is what makes tampering expensive.</>,
                <>Together, they're why a transaction buried under many later blocks is treated as effectively final.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
